/**
 * Studio planner primitives — the mockup kit's own pieces, ported from
 * `.design-shots/redesign-2026-09-09/planner.css` with an `st-` prefix (studio.css §17).
 * See docs/app-map/106-studio-planner-pass.md.
 *
 * These render markup only. Every handler and every piece of state is passed in
 * by the classic component that owns it, so behaviour and `data-testid` values
 * are untouched (105 R2, R3).
 */
import React from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

/** A Studio card: tile + title + optional "small" + right cluster. */
export function StCard({ tile, icon, title, small, right, children, footer, testId, className = '' }) {
  return (
    <section className={`st-card ${className}`.trim()} data-testid={testId}>
      {title != null && (
        <div className="st-wh">
          {icon ? <span className={`st-tile st-tile-${tile || 'planner'}`}>{icon}</span> : null}
          <h2 className="st-wt">{title}{small ? <small>{small}</small> : null}</h2>
          {right ? <div className="st-wr">{right}</div> : null}
        </div>
      )}
      <div className="st-wb">{children}</div>
      {footer ? <div className="st-wf">{footer}</div> : null}
    </section>
  );
}

/** ‹ · Today · › — the period nav from the mock's title row. */
export function StNav({ label, onPrev, onToday, onNext, prevTestId, todayTestId, nextTestId }) {
  return (
    <div className="st-nav">
      <button type="button" className="st-ibtn" aria-label="Previous" onClick={onPrev} data-testid={prevTestId}>
        <ChevronLeft />
      </button>
      {onToday ? (
        <button type="button" className="st-btn st-sec" onClick={onToday} data-testid={todayTestId}>{label}</button>
      ) : null}
      <button type="button" className="st-ibtn" aria-label="Next" onClick={onNext} data-testid={nextTestId}>
        <ChevronRight />
      </button>
    </div>
  );
}

/** The dashed ghost "add" row that replaces the mock's empty states. */
export function StAddRow({ label, onClick, testId, withCheckbox = true }) {
  return (
    <button type="button" className="st-addrow" onClick={onClick} data-testid={testId}>
      {withCheckbox ? <span className="st-cb" /> : <Plus />}
      {label}
    </button>
  );
}

/** The mock draws priority as a 6px dot: red high, amber medium, else the control line. */
export const priorityDot = (priority) => {
  if (priority === 'high') return 'var(--st-bad)';
  if (priority === 'medium' || priority === 'med') return 'var(--st-warn)';
  if (priority === 'low') return 'var(--st-line2)';
  return 'var(--st-line2)';
};

/** Initials avatar, matching §16.4. */
export function StAvatar({ name, url, className = '' }) {
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <span className={`st-av ${className}`.trim()} aria-hidden="true">
      {url ? <img src={url} alt="" /> : initials}
    </span>
  );
}
